import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { NotificationService } from '../services';
import { NOTIFICATION_USER_KEY, NOTIFICATION_COUNT_KEY, NOTIFICATION_LIST_KEY } from '../constants/query';
import { INotification } from '../interfaces/INotification';

interface UseDeleteNotificationOptions {
  showToast?: boolean;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export const useDeleteNotification = (options?: UseDeleteNotificationOptions) => {
  const { showToast = false, onSuccess, onError } = options || {};
  const queryClient = useQueryClient();
  const { t } = useTranslation('notification');

  return useMutation({
    mutationFn: (notificationId: string) => NotificationService.removeNotification(notificationId),
    onMutate: async (notificationId: string) => {
      await queryClient.cancelQueries({ queryKey: [NOTIFICATION_USER_KEY] });
      await queryClient.cancelQueries({ queryKey: [NOTIFICATION_COUNT_KEY] });
      await queryClient.cancelQueries({ queryKey: [NOTIFICATION_LIST_KEY] });

      const previousNotifications = queryClient.getQueryData([NOTIFICATION_USER_KEY]);
      const previousCount = queryClient.getQueryData([NOTIFICATION_COUNT_KEY]);

      queryClient.setQueriesData({ queryKey: [NOTIFICATION_USER_KEY] }, (oldData: any) => {
        if (!oldData?.data) return oldData;

        return {
          ...oldData,
          data: oldData.data.filter((n: INotification) => n._id !== notificationId),
          total: Math.max((oldData.total || 0) - 1, 0),
        };
      });

      queryClient.setQueriesData({ queryKey: [NOTIFICATION_COUNT_KEY] }, (oldCount: any) => {
        const currentCount = typeof oldCount === 'object' && oldCount !== null && 'count' in oldCount
          ? oldCount.count
          : (oldCount || 0);
        return Math.max(currentCount - 1, 0);
      });

      return { previousNotifications, previousCount };
    },
    onError: (error, _, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData([NOTIFICATION_USER_KEY], context.previousNotifications);
      }
      if (context?.previousCount) {
        queryClient.setQueryData([NOTIFICATION_COUNT_KEY], context.previousCount);
      }

      if (showToast) {
        toast.error(t('errorDeletingNotification'));
      }

      onError?.(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_USER_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_COUNT_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_LIST_KEY] });

      onSuccess?.();
    },
  });
};
